import { useCallback, useState } from 'react'
import {
  FileText,
  Image,
  Type,
  X,
  Play,
  Loader2,
  Check,
  AlertCircle,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { useContentCpStore, type QueuedSource, type SourceStatus } from '@/stores/useContentCpStore'
import { cn } from '@/lib/utils'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function SourceIcon({ type, status }: { type: QueuedSource['type']; status: SourceStatus }) {
  const iconClass = cn(
    'w-4 h-4 flex-shrink-0',
    status === 'complete' && 'text-green-500',
    status === 'error' && 'text-destructive',
    status === 'processing' && 'text-primary',
    status === 'queued' && 'text-muted-foreground'
  )

  if (status === 'processing') {
    return <Loader2 className={cn(iconClass, 'animate-spin')} strokeWidth={1.5} />
  }
  if (status === 'complete') {
    return <Check className={iconClass} strokeWidth={2} />
  }
  if (status === 'error') {
    return <AlertCircle className={iconClass} strokeWidth={1.5} />
  }

  // Default icons by type
  switch (type) {
    case 'file':
      return <FileText className={iconClass} strokeWidth={1.5} />
    case 'image':
      return <Image className={iconClass} strokeWidth={1.5} />
    case 'text':
      return <Type className={iconClass} strokeWidth={1.5} />
    default:
      return <FileText className={iconClass} strokeWidth={1.5} />
  }
}

function SourceQueueItem({
  source,
  onRemove,
  isProcessing,
}: {
  source: QueuedSource
  onRemove: (id: string) => void
  isProcessing: boolean
}) {
  const displayName = source.filename || source.title || source.textPreview || 'Source'
  // Shorter truncation on mobile
  const truncatedName = displayName.length > 30 ? displayName.slice(0, 27) + '...' : displayName

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md border transition-colors',
        source.status === 'queued' && 'border-border bg-muted/30',
        source.status === 'processing' && 'border-primary/50 bg-primary/5',
        source.status === 'complete' && 'border-green-500/30 bg-green-500/5',
        source.status === 'error' && 'border-destructive/30 bg-destructive/5'
      )}
    >
      <SourceIcon type={source.type} status={source.status} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 sm:gap-2">
          <span
            className={cn(
              'text-xs sm:text-sm truncate',
              source.status === 'complete' && 'text-green-600 dark:text-green-400',
              source.status === 'error' && 'text-destructive'
            )}
          >
            {truncatedName}
          </span>
          {source.fileSize && (
            <span className="text-xs text-muted-foreground flex-shrink-0 hidden sm:inline">
              {formatFileSize(source.fileSize)}
            </span>
          )}
        </div>

        {/* Status line */}
        {source.status === 'processing' && (
          <div className="text-xs text-primary mt-0.5">Processing...</div>
        )}
        {source.status === 'complete' && source.cardsCreated !== undefined && (
          <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
            {source.cardsCreated} cards, {source.edgesCreated || 0} edges
          </div>
        )}
        {source.status === 'error' && source.error && (
          <div className="text-xs text-destructive mt-0.5 truncate">{source.error}</div>
        )}
      </div>

      {/* Remove button - only show when not processing */}
      {!isProcessing && source.status === 'queued' && (
        <button
          onClick={() => onRemove(source.id)}
          className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
          title="Remove source"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
    </div>
  )
}

interface SourceQueueProps {
  onRunBatch: () => void
  className?: string
}

export function SourceQueue({ onRunBatch, className }: SourceQueueProps) {
  const { sourceQueue, batchState, removeSource, clearSources } = useContentCpStore()
  const [isExpanded, setIsExpanded] = useState(true)

  const handleRemove = useCallback(
    (id: string) => {
      removeSource(id)
    },
    [removeSource]
  )

  const handleClear = useCallback(() => {
    clearSources()
  }, [clearSources])

  const queuedCount = sourceQueue.filter((s) => s.status === 'queued').length
  const completedCount = sourceQueue.filter((s) => s.status === 'complete').length
  const errorCount = sourceQueue.filter((s) => s.status === 'error').length
  const totalCount = sourceQueue.length
  const isProcessing = batchState.isProcessing

  if (totalCount === 0) {
    return null
  }

  return (
    <div className={cn('border border-border rounded-lg overflow-hidden bg-background flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border flex-shrink-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          )}
          <span className="hidden sm:inline">Sources</span>
          <span className="text-xs text-muted-foreground">
            ({completedCount}/{totalCount})
          </span>
          {isProcessing && (
            <span className="text-xs text-primary ml-1">
              <Loader2 className="w-3 h-3 inline animate-spin" />
            </span>
          )}
          {!isProcessing && completedCount === totalCount && totalCount > 0 && errorCount === 0 && (
            <Check className="w-3.5 h-3.5 text-green-500 ml-1" strokeWidth={2} />
          )}
          {errorCount > 0 && (
            <span className="text-xs text-destructive ml-1">
              {errorCount} error{errorCount > 1 ? 's' : ''}
            </span>
          )}
        </button>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Clear button - only when not processing */}
          {!isProcessing && (
            <button
              onClick={handleClear}
              className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors"
              title="Clear all"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}

          {/* Run button */}
          <button
            onClick={onRunBatch}
            disabled={isProcessing || queuedCount === 0}
            className={cn(
              'flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors',
              isProcessing
                ? 'bg-primary/20 text-primary cursor-not-allowed'
                : queuedCount === 0
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-foreground text-background hover:bg-foreground/90'
            )}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
                <span className="hidden sm:inline">Processing...</span>
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5" strokeWidth={2} />
                Run
              </>
            )}
          </button>
        </div>
      </div>

      {/* Source list - collapsible */}
      {isExpanded && (
        <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-1">
          {sourceQueue.map((source) => (
            <SourceQueueItem
              key={source.id}
              source={source}
              onRemove={handleRemove}
              isProcessing={isProcessing}
            />
          ))}
        </div>
      )}

      {/* Summary (after batch complete) - always show if batch complete */}
      {!isProcessing && completedCount === totalCount && totalCount > 0 && (
        <div className="px-3 py-2 bg-green-500/10 border-t border-green-500/20 flex-shrink-0">
          <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">
            Total: {batchState.totalCards} cards, {batchState.totalEdges} edges
          </div>
        </div>
      )}
    </div>
  )
}
